<template>
  <div class="wildfire-farm-map">
    <div ref="mapEl" class="wildfire-farm-map__canvas" :style="{ height: height + 'px' }"></div>
    <div v-if="loading" class="wildfire-farm-map__loading d-flex align-center justify-center">
      <v-progress-circular indeterminate size="26" width="3" color="primary" />
    </div>
    <div class="wildfire-farm-map__opacity-control">
      <span class="text-caption">{{ opacityLabel }}</span>
      <input v-model.number="fillOpacity" type="range" min="0.1" max="0.9" step="0.05" />
    </div>
  </div>
</template>

<script>
import { getGoogleMapsLoader } from '@/mixins/GoogleMapLoaderSingleton'

export default {
  name: 'WildfireFarmMap',
  props: {
    // Polygon ring as [{latitude, longitude}]
    coordinates: { type: Array, default: () => [] },
    centerLat: { type: Number, default: null },
    centerLng: { type: Number, default: null },
    radiusKm: { type: Number, default: null },
    // [minLng, minLat, maxLng, maxLat]
    bbox: { type: Array, default: null },
    fillColor: { type: String, default: '#FFA826' },
    height: { type: Number, default: 260 },
    opacityLabel: { type: String, default: 'Overlay opacity' },
  },
  data() {
    return {
      loading: true,
      google: null,
      map: null,
      shape: null,
      marker: null,
      fillOpacity: 0.45,
    }
  },
  watch: {
    fillOpacity(val) {
      if (this.shape) this.shape.setOptions({ fillOpacity: val })
    },
    fillColor(val) {
      if (this.shape) this.shape.setOptions({ fillColor: val, strokeColor: val })
    },
    coordinates() {
      this.redraw()
    },
    centerLat() {
      this.redraw()
    },
    radiusKm() {
      this.redraw()
    },
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    this.clearShape()
  },
  methods: {
    async initMap() {
      if (!this.$refs.mapEl) {
        this.loading = false
        return
      }
      try {
        this.loading = true
        const loader = getGoogleMapsLoader()
        this.google = await loader.load()
        this.map = new this.google.maps.Map(this.$refs.mapEl, {
          mapTypeId: this.google.maps.MapTypeId.SATELLITE,
          center: this.fallbackCenter(),
          zoom: 15,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'cooperative',
        })
        this.drawShape()
      } catch (e) {
        console.error('WildfireFarmMap init error', e)
      } finally {
        this.loading = false
      }
    },
    fallbackCenter() {
      if (this.coordinates.length) {
        return { lat: this.coordinates[0].latitude, lng: this.coordinates[0].longitude }
      }
      return { lat: this.centerLat || 0, lng: this.centerLng || 0 }
    },
    clearShape() {
      if (this.shape) {
        this.shape.setMap(null)
        this.shape = null
      }
      if (this.marker) {
        this.marker.setMap(null)
        this.marker = null
      }
    },
    redraw() {
      if (!this.map || !this.google) return
      this.clearShape()
      this.drawShape()
    },
    drawShape() {
      if (!this.map || !this.google) return

      if (this.coordinates.length >= 3) {
        const path = this.coordinates.map(c => ({ lat: c.latitude, lng: c.longitude }))
        this.shape = new this.google.maps.Polygon({
          paths: path,
          strokeColor: this.fillColor,
          strokeWeight: 2,
          fillColor: this.fillColor,
          fillOpacity: this.fillOpacity,
          clickable: false,
          map: this.map,
        })
        if (this.bbox) this.fitToBbox()
        else this.fitToPath(path)
        return
      }

      if (this.centerLat != null && this.centerLng != null && this.radiusKm) {
        const center = { lat: this.centerLat, lng: this.centerLng }
        this.shape = new this.google.maps.Circle({
          center,
          radius: this.radiusKm * 1000,
          strokeColor: this.fillColor,
          strokeWeight: 2,
          fillColor: this.fillColor,
          fillOpacity: this.fillOpacity,
          clickable: false,
          map: this.map,
        })
        this.marker = new this.google.maps.Marker({
          position: center,
          map: this.map,
          icon: {
            path: this.google.maps.SymbolPath.CIRCLE,
            scale: 6,
            fillColor: '#D32F2F',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
          },
          zIndex: 5,
        })
        if (this.bbox) this.fitToBbox()
        else this.map.fitBounds(this.shape.getBounds())
      }
    },
    fitToPath(path) {
      const bounds = new this.google.maps.LatLngBounds()
      path.forEach(p => bounds.extend(p))
      this.map.fitBounds(bounds)
    },
    fitToBbox() {
      const [minLng, minLat, maxLng, maxLat] = this.bbox
      const bounds = new this.google.maps.LatLngBounds(
        { lat: minLat, lng: minLng },
        { lat: maxLat, lng: maxLng },
      )
      this.map.fitBounds(bounds)
    },
  },
}
</script>

<style scoped>
.wildfire-farm-map {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: #eceff1;
}

.wildfire-farm-map__canvas {
  width: 100%;
  min-height: 200px;
  display: block;
}

.wildfire-farm-map__loading {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  z-index: 2;
}

.wildfire-farm-map__opacity-control {
  position: absolute;
  left: 8px;
  bottom: 8px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 8px;
  padding: 4px 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.wildfire-farm-map__opacity-control input[type='range'] {
  width: 90px;
}
</style>
